/* eslint-disable class-methods-use-this */
import { Moment } from 'moment';
import uniqBy from 'lodash/uniqBy';

import {
  ProposalDAOInterface,
  DisclaimerConfig,
  ComparisonItemBasedProposalDAOInterface,
  BMOGIFBasedProposalDAOInterface,
  ProposalDAOInterfaces,
} from './ProposalDAO';
import * as ProposalNS from '../Proposal';
import * as ProposalElementNS from '../ProposalElements';
import { ComparisonItemDAO, BMOGIFItemDAO } from './ProposalItemDAO';
import { ProposalFieldDAO } from './ProposalFieldDAO';
import { ProposalUtils } from '../ProposalUtils';
import { ProposalItemLabelCell, ProposalFieldLabelCell, TextCell } from '../../../views/components/cells/index';
import { ScatterPlotOptions } from '../components/views/ProposalScatterPlot';
import { ProposalStyleClassNames } from '../ProposalStyleClassNames';
import { formatLocalizedString, formatMessageWithLocale, formatNumber, formatPercentage, formatDateWithLocale } from '../../../utils/i18n';
import { PROPOSAL_DISCLAIMERS } from '../utils/constants';
import { processWildcardString } from '../utils/proposal';
import Nullable from '@/types/nullable';
import Dictionary from '@/types/dictionary';
import ProposalTableData from '@/types/proposal-table-data';
import SeriesData from '@/types/series-data';
import DonutData from '@/types/donut-data';
import LineSeriesData from '@/types/line-series-data';
import ScatterPlotData from '@/types/scatter-plot-data';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import ProposalTableCellData from '@/types/proposal-table-cell-data';
import DonutHeader from '@/types/donut-header';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import ComparisonItem from '@/types/comparison-item';
import { deepClone } from '@/utils/objects';
import { applyUniverseExclusionToItems } from '@/utils/comparison';
import ProposalToolbarIndexedOption from '@/types/proposal-toolbar-indexed-option';

export class ProposalDAOFieldName {
  fieldName: string;

  className?: string;

  // non localized key - if any
  color?: string;

  constructor(fieldName: string, className?: string, color?: string) {
    this.fieldName = fieldName;
    this.className = className;
    this.color = color;
  }
}

export abstract class BaseProposalDAO implements ProposalDAOInterface {
  // BaseProposalDAO

  proposal: ProposalNS.Proposal;

  proposalElement: ProposalElementNS.ProposalDataElement;

  locale: Locale;

  comments: Dictionary<LocalizedString>;

  protected colorsDict: Nullable<Dictionary<string>> = null;

  public readonly buildDAO = (): void => {
    const colorKeys: string[] = this.getColorKeys();

    colorKeys.forEach(colorKey => {
      this.colorsDict = { ...this.colorsDict, ...ProposalUtils.getColors(colorKey, this.proposalElement) };
    });

    this.build();

    this.postBuild();
  };

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    this.proposal = proposal;
    this.proposalElement = proposalElement;
    this.locale = proposal.options.locale;
    this.comments = proposal.options.comments;
  }

  protected abstract build(): void;

  protected abstract postBuild(): void;

  getInterface<T>(id: number): T | undefined {
    switch (id) {
      case ProposalDAOInterfaces.ComparisonItem:
        if ('getItemDAOs' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.BMOGIF:
        if ('getItemDAOs' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.ItemFields:
        if ('getItemFields' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.TableData:
        if ('getTableData' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.Data:
        if ('getSeriesData' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.Texts:
        if ('getTexts' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.ToolbarOption:
        if ('getToolbarOption' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.ScatterChartOptions:
        if ('getScatterChartOptions' in this) return this as unknown as T;
        break;
      case ProposalDAOInterfaces.TextArea:
        if ('getTextAreaInitialValue' in this) return this as unknown as T;
        break;
      default:
        return undefined;
    }

    return undefined;
  }

  protected abstract getColorKeys(): string[];

  getColorsDict(): Nullable<Dictionary<string>> {
    return this.colorsDict;
  }

  protected formatMessage(key: string, values?: Dictionary<string | number>): string {
    return formatMessageWithLocale(key, this.locale, values);
  }

  protected formatDate(date: Date | Moment | undefined, format: string): string {
    if (!date) {
      return '';
    }

    return formatDateWithLocale(date, this.locale, format);
  }

  protected formatLocalizedString(key: LocalizedString): string {
    return formatLocalizedString(key, this.locale);
  }

  protected formatNumber(number: number, decimals: number = 2): string {
    return formatNumber(number, decimals, this.locale);
  }

  protected formatPercentage(number: number, decimals: number = 2): string {
    return formatPercentage(number, decimals, this.locale);
  }

  protected getMessage(localizedString: LocalizedString): string {
    const s: string | undefined = formatLocalizedString(localizedString, this.locale);
    if (s) {
      return s;
    }
    return '';
  }
}

export abstract class ItemBasedProposalDAO extends BaseProposalDAO {
  // ItemBasedProposalDAO

  fieldNames: Array<ProposalDAOFieldName> = [];

  tableOrientationOptions: ProposalNS.TableOrientationDAOOptions | null = null;

  contentCellTypeOptions: ProposalNS.ContentCellDAOOptions | null = null;

  customColumnNames: ProposalNS.CustomColumnNamesDAOOptions | null = null;

  customFieldNames: ProposalNS.CustomFieldNamesDAOOptions | null = null;

  customDisclaimers: ProposalNS.CustomDisclaimerDAOOptions | null = null;

  toolbarOptionConfig: ProposalNS.ToolbarOptionConfig | null = null;

  sectionIteratorIndex: number | null = null;

  protected _itemIndex: number = 0;

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);

    const daoConfig: ProposalNS.BaseDAOConfig = this.proposalElement.DAO as ProposalNS.BaseDAOConfig;
    if (daoConfig.tableOrientationDAOOptions !== undefined) {
      this.tableOrientationOptions = daoConfig.tableOrientationDAOOptions;
    }

    if (daoConfig.contentCellDAOOptions !== undefined) {
      this.contentCellTypeOptions = daoConfig.contentCellDAOOptions;
    }

    if (daoConfig.customColumnNamesDAOOptions !== undefined) {
      this.customColumnNames = daoConfig.customColumnNamesDAOOptions;
    }

    if (daoConfig.customFieldNamesDAOOptions !== undefined) {
      this.customFieldNames = daoConfig.customFieldNamesDAOOptions;
    }

    if (daoConfig.customDisclaimerDAOOptions !== undefined) {
      this.customDisclaimers = daoConfig.customDisclaimerDAOOptions;
    }

    this._itemIndex = 0;
  }

  protected postBuild(): void {}

  abstract getItemFields(index: number): ProposalFieldDAO[];

  abstract getTableData(skipRows?: number, limitRows?: number): ProposalTableData;

  abstract getSeriesData(): SeriesData[][];

  abstract getDonutData(index?: number): DonutData | null;

  /* eslint-disable-next-line class-methods-use-this */
  getTableClassName(): string {
    return '';
  }

  getLineSeries(): LineSeriesData[] {
    const out: LineSeriesData[] = [];
    return out;
  }

  getScatterSeries(): ScatterPlotData[] {
    const out: ScatterPlotData[] = [];
    return out;
  }

  getScatterChartOptions(): ScatterPlotOptions {
    return {};
  }

  setSectionIteratorIndex(index: Nullable<number>): void {
    this.sectionIteratorIndex = index;
  }

  getSectionIteratorIndex(): Nullable<number> {
    return this.sectionIteratorIndex;
  }

  getToolbarOption(): ProposalToolbarOption | undefined {
    if (this.proposalElement.elementType !== ProposalElementNS.ProposalElementType.Data) {
      return undefined;
    }
    const key = this.toolbarOptionConfig && this.toolbarOptionConfig.key;

    if (!key || !this.proposal.toolbarOptions || !this.proposal.toolbarOptions[key]) {
      return undefined;
    }

    return this.proposal.toolbarOptions[key];
  }

  setToolbarOption(options: ProposalToolbarOption): void {
    if (this.proposalElement.elementType === ProposalElementNS.ProposalElementType.Data) {
      const key = this.toolbarOptionConfig && this.toolbarOptionConfig.key;
      if (key) {
        this.proposal.toolbarOptions[key] = options;
      }
    }
  }

  setIndexedToolbarOption(options: ProposalToolbarIndexedOption): void {
    if (this.proposalElement.elementType === ProposalElementNS.ProposalElementType.Data && this.sectionIteratorIndex != null) {
      const key = this.toolbarOptionConfig && this.toolbarOptionConfig.key;
      if (key) {
        if (this.proposal.toolbarOptions[key].indexedOptions == null) {
          this.proposal.toolbarOptions[key].indexedOptions = {};
        }
        this.proposal.toolbarOptions[key].indexedOptions![`${this.sectionIteratorIndex}`] = options;
      }
    }
  }

  protected addFieldName(fieldName: string, className?: string, color?: string): string {
    if (this._itemIndex < 2) {
      const index: number = this.fieldNames.length;

      let name = fieldName;
      if (this.customFieldNames !== null && this.customFieldNames.names.length > index) {
        name = this.formatLocalizedString(this.customFieldNames.names[index]);
      }

      this.fieldNames.push(new ProposalDAOFieldName(name, className, color));
    }

    return fieldName;
  }
}

export abstract class ProposalDAO extends BaseProposalDAO {
  // ProposalDAO

  protected postBuild(): void {}
}

export abstract class ComparisonItemBasedProposalDAO extends ItemBasedProposalDAO implements ComparisonItemBasedProposalDAOInterface {
  private comparisonItemDAOs: Array<ComparisonItemDAO>;

  private disclaimerConfigs: Array<DisclaimerConfig> = [];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);

    this.toolbarOptionConfig = proposalElement.toolbarOptionConfig ? deepClone(proposalElement.toolbarOptionConfig) : null;

    this.comparisonItemDAOs = [];

    const daoConfig: ProposalNS.BaseDAOConfig = this.proposalElement.DAO as ProposalNS.BaseDAOConfig;

    // Apply filtering to get the final list of items
    let items = this.applyAllowedItemTypesFilter(this.proposal.items, proposalElement);
    items = this.applyUniverseExclusionFilter(items, daoConfig);

    // Apply index-based filtering and create item DAOs
    this.applyFilterItemsByIndexOptions(items, daoConfig);
  }

  protected postBuild(): void {
    if (this.customDisclaimers !== null) {
      for (const customConfig of this.customDisclaimers.configs) {
        switch (customConfig.type) {
          case ProposalNS.CustomDisclaimerType.ADD: {
            this.disclaimerConfigs.push({
              key: customConfig.key || PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER,
              config: {
                customString: customConfig.customString,
                isHTML: !!customConfig.isHTML,
                useDates: !!customConfig.useDates,
                useDetailedDates: !!customConfig.useDetailedDates,
                doNotMergeOnPage: !!customConfig.doNotMergeOnPage,
              },
              items: this.comparisonItemDAOs.map(dao => dao.item),
            });
            break;
          }
          case ProposalNS.CustomDisclaimerType.REPLACE: {
            for (const disclaimerConfig of this.disclaimerConfigs) {
              if (disclaimerConfig.key === customConfig.key) {
                disclaimerConfig.key = PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER;
                disclaimerConfig.config = {
                  customString: customConfig.customString,
                  ...(disclaimerConfig?.config?.interval && { interval: disclaimerConfig.config.interval }),
                  ...(customConfig.datesItemPrefix && { datesItemPrefix: customConfig.datesItemPrefix }),
                  ...(customConfig.doNotMergeOnPage && { doNotMergeOnPage: true }),
                  ...(customConfig.isHTML && { isHTML: true }),
                  ...(customConfig.useDates && { useDates: true }),
                  ...(customConfig.useDetailedDates && { useDetailedDates: true }),
                  ...(customConfig.usePerformanceDates && { usePerformanceDates: true }),
                  ...(customConfig.useRiskDates && { useRiskDates: true }),
                  ...(customConfig.useRollingDates && { useRollingDates: true }),
                };
              }
            }
            break;
          }
          case ProposalNS.CustomDisclaimerType.REMOVE: {
            const index = this.disclaimerConfigs.findIndex(d => d.key === customConfig.key);
            if (index !== -1) {
              this.disclaimerConfigs.splice(index, 1);
            }
            break;
          }
          case ProposalNS.CustomDisclaimerType.CHANGE_KEY: {
            for (const disclaimerConfig of this.disclaimerConfigs) {
              if (disclaimerConfig.key === customConfig.key) {
                disclaimerConfig.key = customConfig.newKey || disclaimerConfig.key;
              }
            }
            break;
          }
        }
      }
    }
  }

  protected nextItem(): ComparisonItemDAO | null {
    if (this._itemIndex < this.comparisonItemDAOs.length) {
      return this.comparisonItemDAOs[this._itemIndex++];
    }
    return null;
  }

  protected itemIndex(): number {
    return this._itemIndex - 1;
  }

  protected resetItemIndex() {
    this._itemIndex = 0;
  }

  getItemDAOs(): Array<ComparisonItemDAO> {
    return this.comparisonItemDAOs;
  }

  getItemFields(index: number = 0): ProposalFieldDAO[] {
    const item = this.comparisonItemDAOs[index];
    return (item && item.getFieldDAOs()) || [];
  }

  getDisclaimerConfigs(): Array<DisclaimerConfig> {
    return this.disclaimerConfigs;
  }

  addDisclaimerConfigs(disclaimerConfigs: DisclaimerConfig[]): void {
    disclaimerConfigs.forEach(disclaimerConfig => {
      disclaimerConfig.items.forEach(item => {
        if (item.type === ProposalNS.ProposalItemType.Benchmark) {
          this.disclaimerConfigs.push({ key: PROPOSAL_DISCLAIMERS.BENCHMARKS, items: [item], config: {} });
          if (this.proposal.template.benchmarksBreakdownDisclaimers != null) {
            this.disclaimerConfigs.push({ key: PROPOSAL_DISCLAIMERS.BENCHMARKS_BREAKDOWN, items: [item], config: { doNotMergeOnPage: true } });
          }
        }
      });

      // TODO: We may want to make this more intelligent and have a central place to define what disclaiers actually use the config and when we should
      // actually run the config compare. That said I thik stripping out the config here is better than adding the logic in the DAOs as this
      // would mean to have it in multiple places.
      const localDisclaimerConfig = disclaimerConfig;
      if (localDisclaimerConfig.key === PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS) {
        localDisclaimerConfig.config = {};
      }

      // do we have the same key -> not testing the config as we do not need it now, but we may at some point
      const existingConfig: DisclaimerConfig | undefined = this.disclaimerConfigs.find(
        thisDisclaimerConfig => thisDisclaimerConfig.key === localDisclaimerConfig.key,
      );
      if (existingConfig === undefined) {
        this.disclaimerConfigs.push(localDisclaimerConfig);
      } else {
        localDisclaimerConfig.items.forEach(newItem => {
          if (existingConfig.items.find(item => item === newItem) === undefined) {
            existingConfig.items.push(newItem);
            existingConfig.items = uniqBy(existingConfig.items, '_id');
          }
        });
      }
    });
  }

  addDisclaimerKeys(disclaimerKeys: string[], item?: ComparisonItem): void {
    const items: ComparisonItem[] = item !== undefined ? [item] : [];
    this.addDisclaimerConfigs(disclaimerKeys.map(disclaimerKey => ({ key: disclaimerKey, items: [...items], config: {} })));
  }

  addFieldDAO(itemDAO: ComparisonItemDAO, field: ProposalFieldDAO, col: number = 0) {
    let { label } = field;
    if (this.customFieldNames !== null) {
      const index: number = itemDAO.getFieldDAOs().length;
      if (this.customFieldNames.names.length > index) {
        label = this.formatLocalizedString(this.customFieldNames.names[index]);
        /* eslint-disable-next-line no-param-reassign */
        field.label = label;
      }
    }
    itemDAO.addFieldDAO(field, col);
  }

  getTableData(skipRows?: number, limitRows?: number): ProposalTableData {
    // Null header since the first column has the field names
    const NULL_HEADER = {
      value: this.formatMessage('Common.Fields'),
      className: 'hidden',
    };

    const filterRows = (rows: ProposalTableRowData[]) => {
      let outRows = rows;
      if (skipRows) {
        outRows = outRows.slice(skipRows);
      }

      if (limitRows) {
        outRows = outRows.slice(0, limitRows);
      }

      return outRows;
    };

    const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();
    const { fieldNames } = this;

    const cellTypeOption: ProposalNS.ContentCellDAOOptions | null = this.contentCellTypeOptions;
    if (cellTypeOption) {
      this.getItemDAOs().forEach(itemDAO => {
        itemDAO.getFieldDAOs().forEach(fieldDAO => {
          /* eslint-disable-next-line no-param-reassign */
          fieldDAO.cellType = cellTypeOption.cellType;
          /* eslint-disable-next-line no-param-reassign */
          fieldDAO.value = { weight: fieldDAO.value, name: fieldDAO.label };
        });
      });
    }

    if (this.tableOrientationOptions && this.tableOrientationOptions.orientation === 'HORIZONTAL') {
      const valueSets: Array<ProposalTableRowData> = [];
      itemDAOs.forEach(item => {
        valueSets.push(...item.getTableColumnData());
      });

      const rowLabels: Array<ProposalTableCellData> = [];
      for (const item of itemDAOs) {
        rowLabels.push({
          value: formatLocalizedString(item.item.name, this.locale),
          color: item.item.color,
          cellType: ProposalItemLabelCell,
          itemType: item.item.type,
          currency: this.proposal.showBaseCurrencies === true ? item.item.currency : undefined,
          clamp: 2,
          className: 'item-name',
          flags: item.item.flags,
        });

        let current: number = 1;
        while (current < item.getFieldDAOCount()) {
          rowLabels.push(NULL_HEADER);
          current++;
        }
      }

      const unfilteredRows: ProposalTableRowData[] = [];
      let headerCells: ProposalTableCellData[] = [];

      if (this.customColumnNames) {
        headerCells = this.customColumnNames.columns.map(column => {
          let nameStr: string = formatLocalizedString(column.name, this.locale);
          nameStr = processWildcardString(nameStr, this.proposal, this.locale);
          return {
            value: nameStr,
            cellType: TextCell,
            className: column.className,
          };
        });
      } else {
        fieldNames.forEach(fieldName => {
          const labelCellClassName = [ProposalStyleClassNames.ProposalTableFieldName, fieldName.className].join(' ');
          headerCells.push({
            value: fieldName.fieldName,
            color: fieldName.color,
            cellType: ProposalFieldLabelCell,
            className: labelCellClassName,
          });
        });
      }

      valueSets.forEach((valueSet, index) => {
        unfilteredRows.push({
          data: [rowLabels[index] || '', ...valueSet.data],
        });
      });

      const out = {
        header: {
          data: [NULL_HEADER, ...headerCells],
        },
        rows: filterRows(unfilteredRows),
      };

      return out;
    }

    const valueSets: Array<ProposalTableRowData> = [];
    itemDAOs.forEach(item => {
      valueSets.push(...item.getTableColumnData());
    });

    let columnLabels: Array<ProposalTableCellData> = [];
    if (this.customColumnNames) {
      columnLabels = this.customColumnNames.columns.map(column => {
        let nameStr: string = formatLocalizedString(column.name, this.locale);
        nameStr = processWildcardString(nameStr, this.proposal, this.locale);
        return {
          value: nameStr,
          cellType: TextCell,
          className: column.className,
        };
      });
    } else {
      for (const item of itemDAOs) {
        columnLabels.push({
          value: formatLocalizedString(item.item.name, this.locale),
          color: item.item.color,
          cellType: ProposalItemLabelCell,
          itemType: item.item.type,
          currency: this.proposal.showBaseCurrencies === true ? item.item.currency : undefined,
          clamp: 2,
          className: 'item-name',
          flags: item.item.flags,
        });

        let current: number = 1;
        while (current < item.getFieldDAOCount()) {
          columnLabels.push(NULL_HEADER);
          current++;
        }
      }
    }

    const out = {
      header: {
        data: [NULL_HEADER, ...columnLabels],
      },
      rows: filterRows(
        fieldNames.map((fieldName, index) => {
          const labelCellClassName = [ProposalStyleClassNames.ProposalTableFieldName, fieldName.className].join(' ');
          const labelCell: ProposalTableCellData = {
            value: fieldName.fieldName,
            color: fieldName.color,
            cellType: ProposalFieldLabelCell,
            className: labelCellClassName,
          };

          const rowClassName = fieldName.className ? `${fieldName.className}-row` : '';

          return {
            className: rowClassName,
            data: [labelCell, ...valueSets.map(set => set.data[index])],
          };
        }),
      ),
    };

    return out;
  }

  getDonutData(index: number = 0): DonutData | null {
    const item: ComparisonItemDAO = this.getItemDAOs()[index];

    if (item === undefined) {
      return null;
    }

    const data: SeriesData[][] = [item.getSeriesData().filter(d => d.y !== null) as SeriesData[]];
    const donutHeader: DonutHeader = {
      type: item.item.type as ProposalNS.ProposalItemType,
      name: formatLocalizedString(item.item.name, this.locale),
      color: item.item.color,
      currency: item.item.currency,
      flags: item.item.flags,
    };

    return {
      donutHeader,
      data,
      colorsDict: null,
      riskProfile: this.proposal.riskProfile || item.item.riskProfile,
    };
  }

  getGraphConfigOptions(): ProposalNS.ChartConfigOptions {
    return {};
  }

  getSeriesData(): SeriesData[][] {
    return this.getItemDAOs().map(item => item.getSeriesData());
  }

  private applyAllowedItemTypesFilter(items: ComparisonItem[], proposalElement: ProposalElementNS.ProposalDataElement): ComparisonItem[] {
    const allowedItemTypes = ProposalUtils.getAllowedItemTypes(proposalElement);
    return allowedItemTypes == null ? items : items.filter(item => allowedItemTypes.includes(item.type));
  }

  private applyUniverseExclusionFilter(items: ComparisonItem[], daoConfig: ProposalNS.BaseDAOConfig): ComparisonItem[] {
    const excludeUniversesConfig: ProposalNS.ExcludeUniversesDAOOptions | undefined = daoConfig.excludeUniversesDAOOptions;

    if (excludeUniversesConfig === undefined || excludeUniversesConfig.universes.length === 0) {
      return items;
    }

    return applyUniverseExclusionToItems(items, excludeUniversesConfig.universes, 'en-CA', (localizedString, locale) =>
      formatLocalizedString(localizedString, locale as Locale),
    );
  }

  private applyFilterItemsByIndexOptions(items: ComparisonItem[], daoConfig: ProposalNS.BaseDAOConfig): void {
    const filter: ProposalNS.FilterItemsByIndexDAOOptions | undefined = daoConfig.filterItemsByIndexDAOOptions;

    if (filter !== undefined) {
      items
        .filter(item => (filter.itemTypes ? filter.itemTypes.some(type => type === item.type) : true))
        .forEach((item, index) => {
          const localIndex: number =
            this.proposal.template.customParameters && this.proposal.template.customParameters.singleItemShift && items.length === 1 ? index + 1 : index;

          // If we're not using either the `itemIterator` or `selection` options, simply add the new item DAO.
          // As of writing this, this is only used to make use of the `itemTypes` option.
          if (!filter.itemIterator && !filter.selection) {
            this.comparisonItemDAOs.push(new ComparisonItemDAO(item));
            return;
          }

          // here we use the absolute index, not the local one. We do not really care about the shift as the iterator
          // simply displays all available items.
          if (filter.itemIterator !== undefined && index === this.proposal.itemIterators.get(filter.itemIterator)) {
            const proposalItemDAO: ComparisonItemDAO = new ComparisonItemDAO(item);
            this.comparisonItemDAOs.push(proposalItemDAO);
          }

          // absolute selection references must be relative to the singleItemShift, we use localIndex.
          if (filter.selection !== undefined && filter.selection.indexOf(localIndex) !== -1) {
            const proposalItemDAO: ComparisonItemDAO = new ComparisonItemDAO(item);
            this.comparisonItemDAOs.push(proposalItemDAO);
          }
        });
    } else {
      this.comparisonItemDAOs = items.map(item => new ComparisonItemDAO(item));
    }
  }

  /* eslint-disable-next-line class-methods-use-this */
  getTextAreaInitialValue(): string | undefined {
    return '';
  }
}

export abstract class BMOGIFBasedProposalDAO extends ItemBasedProposalDAO implements BMOGIFBasedProposalDAOInterface {
  private bmoGIFItemDAOs: BMOGIFItemDAO[];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);

    this.bmoGIFItemDAOs = [];

    if (this.proposal.gifItems !== undefined) {
      for (const item of this.proposal.gifItems) {
        this.bmoGIFItemDAOs.push(new BMOGIFItemDAO(item));
      }
    }
  }

  protected nextItem(): BMOGIFItemDAO | null {
    if (this._itemIndex < this.bmoGIFItemDAOs.length) {
      return this.bmoGIFItemDAOs[this._itemIndex++];
    }
    return null;
  }

  addFieldDAO(itemDAO: BMOGIFItemDAO, field: ProposalFieldDAO, col: number = 0) {
    let { label } = field;
    if (this.customFieldNames !== null) {
      const index: number = itemDAO.getFieldDAOs().length;
      if (this.customFieldNames.names.length > index) {
        label = this.formatLocalizedString(this.customFieldNames.names[index]);
        /* eslint-disable-next-line no-param-reassign */
        field.label = label;
      }
    }
    itemDAO.addFieldDAO(field, col);
  }

  getItemDAOs(): BMOGIFItemDAO[] {
    return this.bmoGIFItemDAOs;
  }

  // eslint-disable-next-line class-methods-use-this
  getDonutData(): DonutData | null {
    return null;
  }

  getSeriesData(): SeriesData[][] {
    return this.getItemDAOs().map(item => item.getSeriesData().filter(d => d.y !== null) as SeriesData[]);
  }

  getItemFields(index: number = 0): ProposalFieldDAO[] {
    const item = this.bmoGIFItemDAOs[index];
    return (item && item.getFieldDAOs()) || [];
  }

  getTableData(skipRows?: number, limitRows?: number): ProposalTableData {
    if (this.tableOrientationOptions && this.tableOrientationOptions.orientation === 'HORIZONTAL') {
      const { fieldNames } = this;
      const rows: ProposalTableRowData[] = [];
      const item: BMOGIFItemDAO = this.getItemDAOs()[0];
      for (let i = 0; i < fieldNames.length; i++) {
        rows.push({
          data: [
            {
              value: fieldNames[i].fieldName,
              className: fieldNames[i].className,
              cellType: ProposalFieldLabelCell,
            },
            item.getFieldDAOs()[i],
          ],
        });
      }

      const out = {
        rows,
      };

      return out;
    }

    let columnLabels: Array<ProposalTableCellData> = [];
    if (this.customColumnNames) {
      columnLabels = this.customColumnNames.columns.map(column => {
        const nameStr: string = formatLocalizedString(column.name, this.locale);
        const labelCellClassName = [ProposalStyleClassNames.ProposalTableFieldName, column.className].join(' ');
        return {
          value: nameStr,
          color: column.color,
          cellType: ProposalFieldLabelCell,
          className: labelCellClassName,
        };
      });
    }

    const item: BMOGIFItemDAO = this.getItemDAOs()[0];
    const count = item.getFieldDAOs().length;
    const rows = [];
    for (let i = 0; i < count; i++) {
      const data = [];
      const colCount = item.getFieldDAOCount();
      for (let j = 0; j < colCount; j++) {
        data.push(item.getFieldDAOs(j)[i]);
      }
      rows[i] = {
        data,
      };
    }

    const out = {
      header: {
        data: columnLabels,
      },
      rows,
    };

    return out;
  }

  getDisclaimerConfigs(): Array<DisclaimerConfig> {
    return [];
  }
}
